import { Event, Story, StoryVideoSource, VideoSource } from '@src/api/types'
import MuteIcon from '@src/icons/mute'
import UnmuteIcon from '@src/icons/unmute'
import classNames from 'classnames'
import VideoPlayer from './video-player'
import { useState } from 'react'
import { PurchaseButton } from './ppv-purchase-button'
import { useRouter } from 'next/router'
import Button from './ui/button'
import WatchNowIcon from '@src/icons/watch-now'
import { formatDate, isDatePassed, roundTime } from '@src/lib/utils'
import { useVideoSource } from '@src/api/story'
import { LockIcon } from '@src/icons/lock-icon'

interface FeedHeroProps {
  event?: Event
  liveVideo?: StoryVideoSource
  liveStory?: Story
  hideContent?: boolean
  moreDetailsLink?: string
}

const MuteButton = ({
  isMuted,
  onToggleMute,
  className
}: {
  isMuted: boolean
  onToggleMute: () => void
  className?: string
}) => {
  return (
    <button className={classNames('absolute text-white', className)} onClick={onToggleMute}>
      {isMuted ? <MuteIcon /> : <UnmuteIcon />}
    </button>
  )
}

function getEventButtonCopy(event: Event) {
  if (event?.type === 'barstool_tv') {
    switch (event?.status) {
      case 'upcoming':
        return 'Starting Soon'
      case 'live':
        return 'Watch Now'
      case 'ended':
        return 'Replay Available Soon'
      case 'on_demand':
        return 'Watch Replay'
      default:
        return `Buy ${event?.price}`
    }
  } else {
    return `Buy ${event?.price}`
  }
}

function useHeroValues(
  event: Event | undefined,
  liveVideo: StoryVideoSource | undefined,
  liveStory?: Story
): {
  isFreeEvent?: boolean
  description?: string
  buttonCopy?: string
  image: string
  id: string
  name: string
  source?: VideoSource
  isVideoHero: boolean
  isLive?: boolean
  premiereDate?: string
  routerLink?: string
} | null {
  if (event) {
    const isBarstoolTV = event?.type === 'barstool_tv'
    const source = event?.metadata?.promo_video?.source?.primary_source
    return {
      isFreeEvent: isBarstoolTV,
      description: event?.metadata?.hero_copy?.body,
      buttonCopy: getEventButtonCopy(event),
      image: event?.metadata?.promo_poster_url,
      id: event?.id,
      name: event?.name,
      source: source,
      isVideoHero: !!source,
      premiereDate: event?.date,
      isLive: event?.status === 'live'
    }
  }
  // Hide live video for now so Surviving gets priority. Will need to fix later
  // else if (liveVideo) {
  //   return {
  //     image: liveVideo?.poster,
  //     id: liveVideo?.id,
  //     name: liveVideo?.name,
  //     isVideoHero: false,
  //     isLive: liveVideo?.status === 'live',
  //     buttonCopy: liveVideo?.status === 'live' ? 'Watch Now' : 'Starting Soon',
  //     premiereDate: liveVideo?.published_at,
  //     routerLink: '/live'
  //   }
  // }
  else if (liveStory) {
    const premiereDate = liveStory?.post_type_meta?.live?.datetime
    const hasPremiered = premiereDate ? isDatePassed(premiereDate) : true
    return {
      image: liveStory?.thumbnail?.raw_desktop,
      id: liveStory?.id,
      name: liveStory?.title,
      isVideoHero: false,
      isLive: hasPremiered,
      buttonCopy: hasPremiered ? 'Watch Now' : 'Starting Soon',
      premiereDate,
      routerLink: `/on-demand/${liveStory?.id}/${liveStory?.slug}`
    }
  }
  return null
}

function FeedsHero({
  event,
  liveVideo,
  liveStory,
  moreDetailsLink,
  hideContent = false
}: FeedHeroProps) {
  const router = useRouter()
  const [isMuted, setIsMuted] = useState(true)

  function toggleMute() {
    setIsMuted((prev) => !prev)
  }

  const { data: liveStoryVideoSource, error } = useVideoSource(liveStory?.id)
  const heroValues = useHeroValues(event, liveVideo, liveStory)

  if (!heroValues) return null

  const {
    description,
    image,
    isFreeEvent,
    buttonCopy,
    premiereDate,
    id,
    name,
    source,
    isVideoHero,
    routerLink,
    isLive = false
  } = heroValues

  return (
    <section className='pb-7 border-b border-b-[#282828] md:mb-10 md:pb-0 md:border-b-0 md:mt-[58px] md:flex justify-end relative md:ml-8'>
      <div className='relative md:w-3/4'>
        {(liveStoryVideoSource || error) && !isVideoHero && (
          <img
            src={
              liveStory && liveStoryVideoSource?.poster_sources[0]?.src
                ? liveStoryVideoSource?.poster_sources[0]?.src
                : image
            }
            alt='hero-image'
          />
        )}
        {isVideoHero && (
          <>
            <VideoPlayer
              id={id}
              name={name}
              status='on_demand'
              className={
                '[--play-button:none] [--bottom-controls:none] [--seek-backward-button:none] [--seek-forward-button:none] h-[212px] sm:h-[312px] md:h-full w-full [--media-object-fit:cover] [--media-object-position:center]  pointer-events-none !pr-0'
              }
              source={source as VideoSource}
              autoPlay
              muted={isMuted}
              loop
              shouldTrackWatchHistory={false}
            />
            <MuteButton
              isMuted={isMuted}
              onToggleMute={toggleMute}
              className='z-[5] bottom-6 right-6 md:bottom-10 md:right-10 lg:bottom-20 lg:right-20'
            />
          </>
        )}
        <div className='absolute top-0 hidden w-2/5 h-full -left-1 md:block gradient-4' />
        <div className='absolute bottom-0 w-full h-full md:h-1/4 gradient-7' />
      </div>
      <div
        id='ppv-card'
        className={classNames(
          'flex flex-col px-4 md:w-2/5 md:mt-auto md:absolute md:justify-center md:top-0 md:bottom-0 md:left-10 md:max-w-[500px]',
          { '-mt-20 sm:pl-10 md:pl-0': event }
        )}
      >
        {event ? (
          <img
            src={event?.metadata?.logo_url}
            alt='logo'
            className='w-[160px] z-50 mb-4 md:mb-9 md:w-[300px] lg:w-[424px]'
          />
        ) : (
          <h1 className='mb-4 font-sans text-xl font-bold text-white lg:text-[32px] lg:leading-tight lg:mb-6'>
            {name}
          </h1>
        )}
        {premiereDate && (
          <h3 className='mb-2 text-base font-bold text-white lg:text-xl'>
            {formatDate(roundTime(premiereDate) as Date, 'LLLL d, h:mma ZZZZ')}
          </h3>
        )}
        {event && !hideContent && (
          <>
            {description && (
              <div
                className='text-sm text-white leading-[17px] mb-6 max-w-sm lg:mb-8 md:max-w-sm lg:max-w-[520px]'
                dangerouslySetInnerHTML={{ __html: description }}
              />
            )}
            {!isFreeEvent && (
              <div className='flex gap-3'>
                {buttonCopy && (
                  <PurchaseButton
                    playLatestEpisode
                    event={event}
                    className='flex items-center justify-center md:px-5 h-10 sm:max-w-[150px] md:min-w-[150px] md:max-w-none md:w-fit'
                  >
                    {buttonCopy}
                  </PurchaseButton>
                )}
                {moreDetailsLink && (
                  <Button
                    className='flex items-center justify-center rounded md:px-5 h-10 sm:max-w-[150px] md:min-w-[150px] md:max-w-none md:w-fit text-white border-2 border-white text-[13.5px] md:text-[13.5px]'
                    onClick={() => router.push(moreDetailsLink)}
                  >
                    More Details
                  </Button>
                )}
              </div>
            )}
          </>
        )}
        {(liveVideo || liveStory || isFreeEvent) &&
          (isLive ? (
            <Button
              onClick={() => routerLink && router.push(routerLink)}
              className='!rounded text-sm md:max-w-[200px] self-start !p-3'
              primary
            >
              <div className='flex items-center justify-center'>
                {event?.status !== 'ended' && <WatchNowIcon className='mb-0 mr-2' />}
                {buttonCopy}
              </div>
            </Button>
          ) : (
            <div className='flex items-center text-sm text-[#999999] font-medium'>
              <div className='mr-2'>
                <LockIcon />
              </div>
              COMING SOON
            </div>
          ))}
      </div>
    </section>
  )
}

export default FeedsHero
